import { supabase } from '@/lib/supabase';
import { Capacitor } from '@capacitor/core';
import { compareVersions } from '@/models/AppVersion';

/** Détecte une erreur réseau (Failed to fetch, CORS, projet Supabase injoignable). */
function isNetworkError(err: unknown): boolean {
  const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : '';
  const details = err && typeof err === 'object' && 'details' in err ? String((err as { details: string }).details) : '';
  const s = `${msg} ${details}`.toLowerCase();
  return (
    s.includes('failed to fetch') ||
    s.includes('network error') ||
    s.includes('load failed') ||
    (err instanceof TypeError && msg.toLowerCase().includes('fetch'))
  );
}

export interface PlatformConfig {
  platform: string;
  min_version: string;
  is_active: boolean;
  store_url: string;
}

/**
 * Service pour la gestion minimaliste des versions via Supabase
 */
export class VersionService {
  private static instance: VersionService;

  public static getInstance(): VersionService {
    if (!VersionService.instance) {
      VersionService.instance = new VersionService();
    }
    return VersionService.instance;
  }

  /**
   * Récupère la configuration pour la plateforme courante.
   * En cas d'erreur réseau (Failed to fetch, CORS, projet en pause), retourne null sans bloquer l'app.
   */
  async getPlatformConfig(): Promise<PlatformConfig | null> {
    const platform = Capacitor.getPlatform(); // 'ios', 'android', or 'web'

    try {
      const { data, error } = await (supabase as any)
        .from('app_version_configs')
        .select('*')
        .eq('platform', platform)
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        if (isNetworkError(error)) {
          if (import.meta.env.DEV) {
            console.warn('⚠️ [VersionService] Réseau indisponible (dev), skip version config');
          }
        } else {
          console.error('❌ [VersionService] Erreur Supabase:', error);
        }
        return null;
      }

      return data as PlatformConfig;
    } catch (e) {
      if (isNetworkError(e)) {
        if (!import.meta.env.DEV) {
          console.warn('⚠️ [VersionService] Connexion Supabase indisponible (vérifier URL / projet actif / CORS)');
        }
      } else {
        console.error('❌ [VersionService] Erreur inattendue:', e);
      }
      return null;
    }
  }

  /**
   * Vérifie si une mise à jour est nécessaire
   */
  async checkUpdateRequired(currentVersion: string): Promise<{ required: boolean; storeUrl?: string }> {
    const config = await this.getPlatformConfig();
    
    if (!config) return { required: false };

    const isBelowMin = compareVersions(currentVersion, config.min_version) < 0;

    return {
      required: isBelowMin,
      storeUrl: config.store_url
    };
  }
}

export const versionService = VersionService.getInstance();
